import type {
  Competition,
  Match,
  Player,
  PlayerEvent,
  Round,
  Score,
  Team,
  Winner,
} from '../../../../services/interfaces';
import {
  BADGE_FALLBACK,
  CompetitionSportsType,
  HomeAwayTeam,
  LineupStatus,
  MatchPhase,
  MatchStatus,
  MatchType,
  MatchUnitSize,
  PlayerEventSubType,
  RoundPhase,
  TeamCategory,
  TeamType,
  LANGUAGES,
} from '../../../../services/interfaces';
import { getLanguage, translateEntity } from '../../../../services/utils';

import { getMatchWording } from './match-unit.wording';

///////////////////////////////////////////////
//           LAYOUT                          //
/////////////////////////////////////////////*/

const HORIZONTAL_LAYOUT = [MatchUnitSize.XL, MatchUnitSize.M] as MatchUnitSize[];
const VERTICAL_LAYOUT = [
  MatchUnitSize.L,
  MatchUnitSize.S,
  MatchUnitSize.S_PLUS,
  MatchUnitSize.L_PLUS,
] as MatchUnitSize[];
const PLUS_SIZES = [MatchUnitSize.S_PLUS, MatchUnitSize.L_PLUS] as MatchUnitSize[];

export const isHorizontalLayout = (size: MatchUnitSize) => HORIZONTAL_LAYOUT.includes(size);
export const isVerticalLayout = (size: MatchUnitSize) => VERTICAL_LAYOUT.includes(size);
export const isSizePlus = (size: MatchUnitSize) => PLUS_SIZES.includes(size);

///////  End of LAYOUT  ////////

export const getBadgeFallback = (team: Team, competition: Competition, round?: Round): BADGE_FALLBACK => {
  if (isNationsLeague(competition?.id, round)) return BADGE_FALLBACK.UNL_GENERIC_BADGE;
  if (isDomestic(team, competition?.teamCategory)) return BADGE_FALLBACK.CLUB_GENERIC_BADGE;
  return `ntc-generic-badge-0${Math.floor(Math.random() * 4 + 1)}` as BADGE_FALLBACK;
};

export const isFake = (team: Team) => !team?.id || team.typeTeam?.toUpperCase() === TeamType.PLACEHOLDER;

export const isNational = (team: Team, teamCategory: TeamCategory) =>
  [team?.typeTeam?.toUpperCase(), teamCategory].includes(TeamType.NATIONAL);

export const isDomestic = (team: Team, teamCategory: TeamCategory) =>
  team?.typeTeam?.toUpperCase() === TeamType.DOMESTIC || teamCategory === TeamCategory.CLUB;

export const isNationsLeague = (cup: string, round: Round) =>
  (window as unknown as Record<string, unknown>).competitionId === '2014' ||
  `${cup}` === '2014' ||
  (((window as unknown as Record<string, unknown>).competitionId === '18' || `${cup}` === '18') &&
    round?.phase === RoundPhase.PREQUALIFYING);

export const isRegions = (cup: string) => `${cup}` === '22';

export const isFriendly = (match: Match) => match?.competition?.id === '19';

export const isFutsal = (match: Match) =>
  match?.competition?.sportsType?.toUpperCase() === CompetitionSportsType.FUTSAL;

export const isSecondLegMatch = (match: Match) =>
  match?.type === MatchType.SECOND_LEG || `${match?.leg?.number}` === '2';

export const showCountryCode = (team: Team, round: Round, teamCategory?: TeamCategory): boolean =>
  isDomestic(team, teamCategory) && round?.phase === RoundPhase.QUALIFYING && !!team?.countryCode;

export const matchScore = (match: Match, ha: HomeAwayTeam) => match?.score?.total?.[ha];

export const matchFouls = (match: Match, ha: HomeAwayTeam) => match?.fouls?.[ha];

export const matchScorers = (match: Match) => match?.playerEvents?.scorers;

export const getSeasonLabel = (match: Match) => {
  if (!match?.competition || !match?.seasonYear) return;

  const {
    seasonYear,
    competition: { id: competitionId, teamCategory },
  } = match;
  const year = parseInt(seasonYear);
  if (competitionId === '18' && year === 2021) {
    return '2022';
  }

  if (teamCategory === TeamCategory.NATIONAL) {
    return seasonYear;
  }

  if (['1', '14', '28'].includes(competitionId) && year < 2008) {
    return `${year}/${(year + 1).toString().substring(seasonYear.length - 2)}`;
  }

  return `${year - 1}/${seasonYear.substring(seasonYear.length - 2)}`;
};

///////////////////////////////////////////////
//           Match Winner                    //
/////////////////////////////////////////////*/

export const matchWinner = (match: Match, useAggregate = true): Winner =>
  useAggregate ? (match?.winner?.aggregate ?? match?.winner?.match) : match?.winner?.match;

export const isWinner = (match: Match, ha: HomeAwayTeam, useAggregate = isSecondLegMatch(match)): boolean => {
  const winner = matchWinner(match, useAggregate);

  const teamId = match[`${ha}Team`]?.id;
  return winner?.team?.id === teamId;
};

export const matchReasonOfWinDisplayText = (
  match: Match,
  useAggregate = isSecondLegMatch(match),
  showFirstLegReasonWin = false,
): string => {
  const winner = matchWinner(match, useAggregate);
  if (match?.type === MatchType.FIRST_LEG && !showFirstLegReasonWin) return '';

  return translateEntity(winner)?.reasonText ?? translateEntity(winner)?.reasonTextAbbr ?? '';
};

///////  End of Match Winner  ////////

///////////////////////////////////////////////
//      Match Relate and Aggregate Score     //
/////////////////////////////////////////////*/

export const firstLegResultPreMatch = (match: Match): string => {
  if (!isSecondLegMatch(match)) return '';

  const firstLegMatch = match?.relatedMatches?.find((m) => m.type === MatchType.FIRST_LEG) as Match;
  if (!firstLegMatch) return '';

  if (
    firstLegMatch.status === MatchStatus.CANCELED &&
    match?.status !== MatchStatus.CANCELED &&
    (!matchWinner(match)?.reason || match?.status !== MatchStatus.FINISHED)
  ) {
    return getMatchWording('single-leg-match');
  }

  if (!matchWinner(firstLegMatch)) return '';

  const [homeFirstLegScore, awayFirstLegScore] = [
    matchScore(firstLegMatch, HomeAwayTeam.HOME),
    matchScore(firstLegMatch, HomeAwayTeam.AWAY),
  ];
  if (!Number.isInteger(homeFirstLegScore) || !Number.isInteger(awayFirstLegScore)) return '';

  const label = getMatchWording('1stleg');
  return `${label}: ${awayFirstLegScore}-${homeFirstLegScore}`;
};

export const matchAggregatedScore = (score: Score, ha: HomeAwayTeam): number => {
  const teamScore = score?.aggregate?.[ha];
  return Number.isInteger(teamScore) ? teamScore : null;
};

export const matchAggregate = (match: Match): string => {
  if (!isSecondLegMatch(match)) return '';

  const [homeAggregatedScore, awayAggregatedScore] = [
    matchAggregatedScore(match?.score, HomeAwayTeam.HOME),
    matchAggregatedScore(match?.score, HomeAwayTeam.AWAY),
  ];

  if (!Number.isInteger(homeAggregatedScore) || !Number.isInteger(awayAggregatedScore)) return '';

  const label = getMatchWording('aggregate_abbr');
  return `${label}: ${homeAggregatedScore}-${awayAggregatedScore}`;
};

///////  Match Relate and Aggregate Score  ////////

///////////////////////////////////////////////
//           Match live minutes              //
/////////////////////////////////////////////*/

const casesPrintMinute = [
  MatchPhase.FIRST_HALF,
  MatchPhase.SECOND_HALF,
  MatchPhase.EXTRA_TIME_FIRST_HALF,
  MatchPhase.EXTRA_TIME_SECOND_HALF,
  MatchPhase.PAUSE,
] as MatchPhase[];
const casePrintExtraTime = [MatchPhase.EXTRA_TIME_FIRST_HALF, MatchPhase.EXTRA_TIME_SECOND_HALF] as MatchPhase[];

export const matchMinute = (match: Match): string => {
  let result = '';
  if (match?.status !== MatchStatus.LIVE || !casesPrintMinute.includes(match?.phase) || isFutsal(match)) return result;

  const minute = match?.minute?.normal;
  const injury = match?.minute?.injury;

  if (Number.isInteger(minute)) result += `${minute}'`;
  if (injury > 0) result += `+${injury}`;

  if (casePrintExtraTime.includes(match?.phase)) {
    const translation = getMatchWording('extra_time_short');
    result = translation.replace('{minute}', result);
  }

  return result;
};

///////  End of Match live minutes  ////////

///////////////////////////////////////////////
//                CTA                        //
//////////////////////////////////////////////

export const printCta = (match: Match): boolean =>
  (isUpcomingMatch(match.status) && !matchLineupsAvailable(match)) || isFinishedMatch(match.status);

///////  End of CTA  ////////

///////////////////////////////////////////////
//           Line-ups                        //
/////////////////////////////////////////////*/

export const lineupAvailableStates = [LineupStatus.AVAILABLE, LineupStatus.TACTICAL_AVAILABLE] as LineupStatus[];

export const matchLineupsAvailable = (match: Match): boolean => lineupAvailableStates.includes(match?.lineupStatus);

export const lineupStatus = (match: Match): string => (matchLineupsAvailable(match) ? getMatchWording('line-ups') : '');

///////  End of Line-ups  ////////

///////////////////////////////////////////////
//           Match Phase/Status              //
/////////////////////////////////////////////*/

const casesPrintPhaseFutsal = [
  MatchPhase.FIRST_HALF,
  MatchPhase.SECOND_HALF,
  MatchPhase.EXTRA_TIME_FIRST_HALF,
  MatchPhase.EXTRA_TIME_SECOND_HALF,
] as MatchPhase[];
const phaseIsPrintableFutsal = (match: Match): boolean =>
  match?.status === MatchStatus.LIVE && casesPrintPhaseFutsal.includes(match?.phase);

export const casesPrintPhase = [
  MatchPhase.HALF_TIME_BREAK,
  MatchPhase.END_SECOND_HALF,
  MatchPhase.EXTRA_TIME_BREAK,
  MatchPhase.END_EXTRA_TIME_SECOND_HALF,
  MatchPhase.PAUSE,
  MatchPhase.FULL_TIME,
] as MatchPhase[];
export const phaseIsPrintable = (match: Match, showPenaltyPhase = false): boolean => {
  if (isFutsal(match)) return phaseIsPrintableFutsal(match);

  return (
    match?.status === MatchStatus.LIVE &&
    ((showPenaltyPhase && match?.phase === MatchPhase.PENALTY) || casesPrintPhase.includes(match?.phase))
  );
};

const casesPrintStatus = [
  MatchStatus.CANCELED,
  MatchStatus.POSTPONED,
  MatchStatus.ABANDONED,
  MatchStatus.SUSPENDED,
] as MatchStatus[];
export const statusIsPrintable = (match: Match) => casesPrintStatus.includes(match?.status);

///////  End of Match Phase/Stattus  ////////

///////////////////////////////////////////////
//           Match penalties score           //
/////////////////////////////////////////////*/

export const matchPenaltyScore = (match: Match, ha: HomeAwayTeam): number => {
  const score = match?.score?.penalty?.[ha];
  return Number.isInteger(score) ? score : null;
};
export const printPenalty = (match: Match, addSpaces: boolean): string => {
  const [homePenaltyScore, awayPenaltyScore] = [
    matchPenaltyScore(match, HomeAwayTeam.HOME),
    matchPenaltyScore(match, HomeAwayTeam.AWAY),
  ];

  if (!Number.isInteger(homePenaltyScore) || !Number.isInteger(awayPenaltyScore)) return '';

  const lbl = getMatchWording('penalty_abbr');
  const divider = addSpaces ? ' - ' : '-';
  return `(${homePenaltyScore}${divider}${awayPenaltyScore}${lbl})`;
};

///////  End of Match penalties score  ////////

///////////////////////////////////////////////
//           Match penalties live            //
/////////////////////////////////////////////*/

export const matchPenaltyScorers = (match: Match, ha: HomeAwayTeam) =>
  match?.playerEvents?.penaltyScorers?.filter((p) => p.teamId === match[`${ha}Team`].id);

export const getDisplayedPenaltiesCount = (
  homeTeamPenalties: PlayerEvent[],
  awayTeamPenalties: PlayerEvent[],
): number => {
  const homeTeamPenaltiesShot = homeTeamPenalties?.length ?? 0;
  const awayTeamPenaltiesShot = awayTeamPenalties?.length ?? 0;
  const homeTeamScore =
    homeTeamPenalties?.filter((p) =>
      ([PlayerEventSubType.GOAL, PlayerEventSubType.SCORED] as PlayerEventSubType[]).includes(p.penaltyType),
    ).length ?? 0;
  const awayTeamScore =
    awayTeamPenalties?.filter((p) =>
      ([PlayerEventSubType.GOAL, PlayerEventSubType.SCORED] as PlayerEventSubType[]).includes(p.penaltyType),
    ).length ?? 0;

  if (homeTeamPenaltiesShot < 5 && awayTeamPenaltiesShot < 5 && homeTeamScore < 5 && awayTeamScore < 5) return 5;

  if (homeTeamPenaltiesShot === awayTeamPenaltiesShot) {
    return homeTeamScore === awayTeamScore ? homeTeamPenaltiesShot + 1 : homeTeamPenaltiesShot;
  }

  return Math.max(homeTeamPenaltiesShot, awayTeamPenaltiesShot);
};

///////  End of Match penalties live  ////////

///////////////////////////////////////////////
//           Match generic stattus           //
/////////////////////////////////////////////*/

const caseMatchUpcoming = [MatchStatus.UNKNOWN, MatchStatus.UPCOMING] as MatchStatus[];
export const isUpcomingMatch = (status: MatchStatus): boolean => caseMatchUpcoming.includes(status);

export const isLiveMatch = (status: MatchStatus): boolean => status === MatchStatus.LIVE;

export const isLiveMatchPenalties = (status: MatchStatus, phase: MatchPhase) =>
  status === MatchStatus.LIVE && phase === MatchPhase.PENALTY;

export const isFinishedMatch = (status: MatchStatus): boolean => status === MatchStatus.FINISHED;

export const isSuspendedMatch = (status: MatchStatus): boolean => status === MatchStatus.SUSPENDED;

export const isAbondonedMatch = (status: MatchStatus): boolean => status === MatchStatus.ABANDONED;

export const isPostponedMatch = (status: MatchStatus): boolean => status === MatchStatus.POSTPONED;

export const isCanceledMatch = (status: MatchStatus): boolean => status === MatchStatus.CANCELED;

///////  End of Match generic stattus  ////////

///////////////////////////////////////////////
//           Match CSS style variant           //
/////////////////////////////////////////////*/

export const setCssVariant = (size: MatchUnitSize) => ({
  ...(size && { [`size--${size}`]: true }),
});

///////  End of Match CSS style variant  ////////

///////////////////////////////////////////////
//           Match Services URLs             //
/////////////////////////////////////////////*/

const getLocaleUrl = (baseUrl: string) => {
  const locale = getLanguage();
  if (locale === LANGUAGES.EN) return baseUrl;

  return baseUrl.replace('www', locale.toLocaleLowerCase());
};

export const getTeamServiceUrl = (baseUrl: string, teamId: Team['id'], competitionId: Competition['id']) => {
  const url = getLocaleUrl(baseUrl);
  return url.replace('{{teamId}}', teamId).replace('{{competitionId}}', competitionId);
};

export const getPlayerServiceUrl = (baseUrl: string, playerId: Player['id'], competitionId: Competition['id']) => {
  const url = getLocaleUrl(baseUrl);
  return url.replace('{{playerId}}', playerId).replace('{{competitionId}}', competitionId);
};

///////  End of Match Services URLs  ////////
